import React, { useState } from "react";
import { Box, Menu, Text, UnstyledButton } from "@mantine/core";
import { useCalendar } from "../../../../../context/CalendarContext";
import { WeekDisplay } from "../../../../../types/calendar";
import { DESIGN_ICONS } from "../../../../../theme/designIcons";
import styles from "./CalendarToolbar.module.css";

const OPTIONS: {
  value: WeekDisplay;
  label: string;
  sub: string;
  Icon: (typeof DESIGN_ICONS)[keyof typeof DESIGN_ICONS];
}[] = [
  {
    value: "combined",
    label: "Combined calendar",
    sub: "Every provider’s appointments in one week.",
    Icon: DESIGN_ICONS.CalendarDays,
  },
  {
    value: "split",
    label: "Split view",
    sub: "Each provider’s week side by side.",
    Icon: DESIGN_ICONS.Columns3,
  },
];

const { LayoutPanelLeft, ChevronDown, Check } = DESIGN_ICONS;

/**
 * Week-only "Display" dropdown (Combined / Split). Lives in the RBC toolbar,
 * so its value comes from CalendarContext, not props.
 */
const WeekDisplayMenu: React.FC = () => {
  const { weekDisplay, setWeekDisplay } = useCalendar();
  const [open, setOpen] = useState(false);

  return (
    <Menu
      opened={open}
      onChange={setOpen}
      position="bottom-end"
      width={280}
      radius={12}
      shadow="md"
      withinPortal
    >
      <Menu.Target>
        <UnstyledButton
          className={`${styles.control} ${styles.displayControl}`}
          data-open={open || undefined}
          aria-label={`Display: ${weekDisplay === "split" ? "Split view" : "Combined"}`}
        >
          <Box component="span" className={styles.displayIcon}>
            <LayoutPanelLeft size={15} />
          </Box>
          <Box component="span" className={styles.displayKicker}>
            Display
          </Box>
          <Box component="span" className={styles.displayValue}>
            {weekDisplay === "split" ? "Split view" : "Combined"}
          </Box>
          <Box component="span" className={styles.chevron}>
            <ChevronDown size={14} />
          </Box>
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown p={6}>
        <Menu.Label className={styles.displayMenuLabel}>Display</Menu.Label>
        {OPTIONS.map(({ value, label, sub, Icon }) => {
          const on = weekDisplay === value;
          return (
            <Menu.Item
              key={value}
              role="menuitemradio"
              aria-checked={on}
              data-on={on || undefined}
              className={styles.displayOption}
              onClick={() => setWeekDisplay(value)}
              leftSection={
                <Box component="span" className={styles.displayOptionIcon}>
                  <Icon size={16} />
                </Box>
              }
              rightSection={
                <Box component="span" className={styles.displayTick}>
                  <Check size={15} />
                </Box>
              }
            >
              <Text component="span" className={styles.displayOptionLabel}>
                {label}
              </Text>
              <Text component="span" className={styles.displayOptionSub}>
                {sub}
              </Text>
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
};

export default WeekDisplayMenu;
